import { useEffect, useState } from 'react'
import {
  Hash,
  Loader2,
  Phone,
  PhoneOff,
  Plus,
  Search,
  Shield,
  ShoppingCart,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useAvailablePDNumbers,
  type PDNumberCountry,
  useOwnedPDNumbers,
  usePurchasePDNumber,
  type PDNumberSearchType,
} from '@/api/powerDialer'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'
import { trialRefusal } from './trialRefusal'

/**
 * Vue Manager : liste des numéros Power Dialer + dialog d'achat.
 *
 * Permet d'acheter des numéros supplémentaires hors flow d'onboarding.
 * Les numéros sont créés directement sur le subaccount Twilio de la company
 * via POST /api/power-dialer/numbers/purchase/. Le backend configure
 * automatiquement les webhooks voice + status_callback.
 */
export function PdNumbersView() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: numbers = [], isPending } = useOwnedPDNumbers()
  const [buyOpen, setBuyOpen] = useState(false)
  const { allowed, planFor } = usePlanRights()
  const pdPermis = allowed('power_dialer')

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className={cn(
              'text-xl font-semibold',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {t('manager.powerDialer.numbers.title', 'Numéros Power Dialer')}
          </h2>
          <p
            className={cn(
              'text-sm mt-1',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {t(
              'manager.powerDialer.numbers.subtitle',
              'Les numéros utilisés comme caller ID lors de vos campagnes.',
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
        {!pdPermis && <PlanBadge plan={planFor('power_dialer')} />}
        <Button
          onClick={() => setBuyOpen(true)}
          disabled={!pdPermis}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('manager.powerDialer.numbers.buyButton', 'Acheter un numéro')}
        </Button>
        </div>
      </div>

      {isPending ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : numbers.length === 0 ? (
        <div
          className={cn(
            'rounded-2xl border border-dashed text-center py-12 px-4',
            darkMode
              ? 'border-slate-700 bg-slate-900/50'
              : 'border-slate-300 bg-slate-50',
          )}
        >
          <PhoneOff
            className={cn(
              'h-12 w-12 mx-auto mb-3',
              darkMode ? 'text-slate-600' : 'text-slate-400',
            )}
          />
          <p
            className={cn(
              'text-sm font-medium mb-1',
              darkMode ? 'text-slate-300' : 'text-slate-700',
            )}
          >
            {t(
              'manager.powerDialer.numbers.empty',
              'Aucun numéro Power Dialer',
            )}
          </p>
          <p
            className={cn(
              'text-xs mb-4',
              darkMode ? 'text-slate-500' : 'text-slate-500',
            )}
          >
            {t(
              'manager.powerDialer.numbers.emptyDescription',
              'Achetez un premier numéro pour démarrer vos campagnes.',
            )}
          </p>
          {!pdPermis && <PlanBadge plan={planFor('power_dialer')} className="mb-3" />}
          <Button
            onClick={() => setBuyOpen(true)}
            disabled={!pdPermis}
            variant="outline"
            className={darkMode ? 'border-slate-700' : ''}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('manager.powerDialer.numbers.buyFirst', 'Acheter un numéro')}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {numbers.map((num) => (
            <div
              key={num.id}
              className={cn(
                'flex items-center justify-between p-4 rounded-xl border',
                darkMode
                  ? 'border-slate-800 bg-slate-900'
                  : 'border-slate-200 bg-white',
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={cn(
                    'h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0',
                    darkMode
                      ? 'bg-blue-500/10 text-blue-300'
                      : 'bg-blue-50 text-blue-600',
                  )}
                >
                  <Phone className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p
                    className={cn(
                      'font-mono text-sm font-semibold tabular-nums',
                      darkMode ? 'text-white' : 'text-slate-900',
                    )}
                  >
                    {formatPhone(num.phone_number)}
                  </p>
                  {num.label && (
                    <p
                      className={cn(
                        'text-xs',
                        darkMode ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      {num.label}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BuyPdNumberDialog
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        darkMode={darkMode}
      />
    </div>
  )
}

// ============================================================
// Dialog d'achat — 2 tabs (Indicatif / Sans frais), sélection radio,
// bouton "Acheter" en bas. Pattern inspiré des softphones modernes
// (Quo, Aircall) : choix conscient avant la dépense.
// ============================================================

interface BuyDialogProps {
  open: boolean
  onClose: () => void
  darkMode: boolean
}

function BuyPdNumberDialog({ open, onClose, darkMode }: BuyDialogProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<PDNumberSearchType>('local')
  // Pays d'achat : CA (défaut) ou US. Le backend accepte les deux ; présenter
  // un numéro US suppose des prospects US (garde-fou présence locale « même
  // pays »).
  const [country, setCountry] = useState<PDNumberCountry>('CA')
  // Area code optionnel : champ filtre, pas requis pour fetcher.
  // Au mount on demande des numéros quelconques (Twilio retourne 10 dispos).
  const [areaCode, setAreaCode] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  // Auto-search activé dès l'ouverture : on fetch tout de suite pour montrer
  // des numéros, l'user filtre/affine ensuite par area code s'il veut.
  const [searchTriggered, setSearchTriggered] = useState(true)

  const isLocal = tab === 'local'
  const queryEnabled = searchTriggered

  const {
    data: results = [],
    isFetching,
    refetch,
  } = useAvailablePDNumbers({
    areaCode,
    type: tab,
    country,
    enabled: queryEnabled,
  })

  const purchase = usePurchasePDNumber()

  // Re-init à chaque ouverture : area code vide + auto-fetch CA.
  useEffect(() => {
    if (open) {
      setAreaCode('')
      setSelected(null)
      setSearchTriggered(true)
      setTab('local')
      setCountry('CA')
    }
  }, [open])

  const handleCountryChange = (next: PDNumberCountry) => {
    setCountry(next)
    setSelected(null)
    setAreaCode('')
    // La query key inclut le pays → TanStack relance automatiquement.
    setSearchTriggered(true)
  }

  const handleTabChange = (next: string) => {
    setTab(next as PDNumberSearchType)
    setSelected(null)
    // Auto-fetch dans tous les cas : la query key inclut le tab + areaCode,
    // donc TanStack relance automatiquement.
    setSearchTriggered(true)
  }

  const handleSearch = () => {
    // Area code optionnel : si vide, on récupère des numéros CA quelconques.
    // Si fourni, il doit faire 3 chiffres min (validation côté Twilio).
    if (isLocal && areaCode.trim().length > 0 && areaCode.trim().length < 3) {
      toast.error(
        t(
          'manager.powerDialer.numbers.areaCodeError',
          'Indicatif : 3 chiffres min (laisser vide pour tout le pays)',
        ),
      )
      return
    }
    setSelected(null)
    setSearchTriggered(true)
    void refetch()
  }

  const handleBuy = () => {
    if (!selected) return
    purchase.mutate(selected, {
      onSuccess: () => {
        toast.success(
          t('manager.powerDialer.numbers.purchaseSuccess', {
            number: formatPhone(selected),
            defaultValue: 'Numéro {{number}} acheté',
          }),
        )
        onClose()
      },
      onError: (err: unknown) => {
        const message =
          trialRefusal(err, t) ||
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error || t('manager.powerDialer.numbers.purchaseError', 'Achat échoué')
        toast.error(message)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={cn(
          'sm:max-w-lg p-0 gap-0 overflow-hidden',
          darkMode && 'bg-slate-900 border-slate-700 text-slate-100',
        )}
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShoppingCart className="h-5 w-5" />
            {t('manager.powerDialer.numbers.dialogTitle', 'Acheter un numéro')}
          </DialogTitle>
          <DialogDescription
            className={darkMode ? 'text-slate-400' : 'text-slate-500'}
          >
            {t(
              'manager.powerDialer.numbers.dialogDescription2',
              'Choisissez un type de numéro puis sélectionnez-en un dans la liste.',
            )}
          </DialogDescription>
          <p className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            {t(
              'manager.powerDialer.numbers.priceNote',
              '1 numéro inclus par utilisateur. Au-delà : 5 $ par mois par numéro.',
            )}
          </p>
        </DialogHeader>

        {/* Sélecteur de pays : Canada | États-Unis */}
        <div className="px-6 pb-3">
          <div
            className={cn(
              'inline-flex rounded-lg border p-0.5 gap-0.5',
              darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-slate-50',
            )}
          >
            {(
              [
                { value: 'CA' as const, label: t('manager.powerDialer.numbers.countryCA', '🇨🇦 Canada') },
                { value: 'US' as const, label: t('manager.powerDialer.numbers.countryUS', '🇺🇸 États-Unis') },
              ]
            ).map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => handleCountryChange(c.value)}
                className={cn(
                  'px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors',
                  country === c.value
                    ? darkMode
                      ? 'bg-slate-700 text-white'
                      : 'bg-white text-slate-900 shadow-sm'
                    : darkMode
                      ? 'text-slate-400 hover:text-slate-200'
                      : 'text-slate-500 hover:text-slate-700',
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs : Indicatif | Sans frais */}
        <div className="px-6">
          <Tabs value={tab} onValueChange={handleTabChange}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="local" className="gap-1.5">
                <Hash className="h-3.5 w-3.5" />
                {t('manager.powerDialer.numbers.tabLocal', 'Par indicatif')}
              </TabsTrigger>
              <TabsTrigger value="tollfree" className="gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                {t('manager.powerDialer.numbers.tabTollFree', 'Sans frais')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Search bar (uniquement pour le tab local) */}
        <div className="px-6 pt-4 pb-2">
          {isLocal ? (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label htmlFor="area-code" className="text-sm font-medium">
                  {t(
                    'manager.powerDialer.numbers.areaCodeFilterLabel',
                    'Filtrer par indicatif (optionnel)',
                  )}
                </Label>
                <Input
                  id="area-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch()
                  }}
                  placeholder={country === 'US' ? '212, 305, 415…' : '438, 514, 418…'}
                  className="mt-1 tabular-nums"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={
                  isFetching ||
                  (areaCode.length > 0 && areaCode.length < 3)
                }
                variant="outline"
              >
                {isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">
                  {t('common.search', 'Rechercher')}
                </span>
              </Button>
            </div>
          ) : (
            <p
              className={cn(
                'text-xs',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {t(
                'manager.powerDialer.numbers.tollFreeHint',
                'Les numéros sans frais (800, 833, 844, 855, 866, 877, 888) sont disponibles partout au Canada.',
              )}
            </p>
          )}
        </div>

        {/* Liste résultats */}
        <div className="px-6 pb-2">
          <div
            className={cn(
              'rounded-lg border max-h-80 overflow-y-auto min-h-[200px]',
              darkMode ? 'border-slate-700' : 'border-slate-200',
            )}
          >
            {isFetching ? (
              <NumberSkeleton darkMode={darkMode} />
            ) : results.length === 0 ? (
              <p
                className={cn(
                  'text-sm text-center py-12',
                  darkMode ? 'text-slate-500' : 'text-slate-500',
                )}
              >
                {t(
                  'manager.powerDialer.numbers.noResults',
                  'Aucun numéro disponible pour cet indicatif.',
                )}
              </p>
            ) : (
              <ul role="radiogroup" className="divide-y divide-slate-200 dark:divide-slate-700">
                {results.map((num) => {
                  const isSelected = selected === num.phoneNumber
                  return (
                    <li key={num.phoneNumber}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => setSelected(num.phoneNumber)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                          isSelected
                            ? darkMode
                              ? 'bg-blue-500/10'
                              : 'bg-blue-50'
                            : darkMode
                              ? 'hover:bg-slate-800'
                              : 'hover:bg-slate-50',
                        )}
                      >
                        <span
                          className={cn(
                            'h-4 w-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                            isSelected
                              ? 'border-blue-600'
                              : darkMode
                                ? 'border-slate-600'
                                : 'border-slate-300',
                          )}
                        >
                          {isSelected && (
                            <span className="h-2 w-2 rounded-full bg-blue-600" />
                          )}
                        </span>
                        <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                          <span
                            className={cn(
                              'font-mono text-sm font-semibold tabular-nums',
                              darkMode ? 'text-white' : 'text-slate-900',
                            )}
                          >
                            {formatPhone(num.phoneNumber)}
                          </span>
                          {(num.locality || num.region) && (
                            <span
                              className={cn(
                                'text-xs truncate',
                                darkMode ? 'text-slate-400' : 'text-slate-500',
                              )}
                            >
                              {[num.locality, num.region]
                                .filter(Boolean)
                                .join(', ')}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter
          className={cn(
            'px-6 py-4 border-t',
            darkMode ? 'border-slate-800' : 'border-slate-100',
          )}
        >
          <Button
            variant="outline"
            onClick={onClose}
            disabled={purchase.isPending}
            className={darkMode ? 'border-slate-700' : ''}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleBuy}
            disabled={!selected || purchase.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {purchase.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ShoppingCart className="h-4 w-4 mr-2" />
            )}
            {t('manager.powerDialer.numbers.buy', 'Acheter')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function NumberSkeleton({ darkMode }: { darkMode: boolean }) {
  return (
    <ul className="divide-y divide-slate-200 dark:divide-slate-700">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 px-4 py-3 animate-pulse"
        >
          <span
            className={cn(
              'h-4 w-4 rounded-full',
              darkMode ? 'bg-slate-700' : 'bg-slate-200',
            )}
          />
          <span
            className={cn(
              'h-3 w-32 rounded',
              darkMode ? 'bg-slate-700' : 'bg-slate-200',
            )}
          />
          <span
            className={cn(
              'h-3 w-24 rounded ml-auto',
              darkMode ? 'bg-slate-800' : 'bg-slate-100',
            )}
          />
        </li>
      ))}
    </ul>
  )
}

/**
 * Formate un numéro E.164 NANP en présentation humaine.
 * `+14389298446` → `(438) 929-8446`.
 * Garde le format E.164 brut si ce n'est pas du NANP (+1 + 10 digits).
 */
function formatPhone(e164: string): string {
  const match = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  if (match) return `(${match[1]}) ${match[2]}-${match[3]}`
  return e164
}
